import type { MRT_ColumnDef, MRT_GroupingState, MRT_Row } from "material-react-table";
import { MRT_ExpandAllButton, MRT_ExpandButton } from "material-react-table";
import { Box, IconButton, Stack } from "@mui/material";
import { Download } from "@mui/icons-material";

import type { Job } from "../../../models";
import {
	checkboxMultiselectColumn,
	textColumn,
	multiSelectColumn,
	numericColumn,
	dateColumn,
	checkboxColumn,
} from "./columns";
import { PortalTooltip } from "../../helpers";

interface BuildColumnsParams {
	acresAggregation: "total" | "average";
	setAcresAggregation: React.Dispatch<React.SetStateAction<"total" | "average">>;
	getBranchField: (job: Job) => string;
	getRegionField: (job: Job) => string;
	jobStatusList: string[];
	testPackageList: string[];
	submitterList: string[];
	seasonList: string[];
	branchList: string[];
	regionList: string[];
}

export function buildColumns(params: BuildColumnsParams): MRT_ColumnDef<Job>[] {
	const {
		acresAggregation,
		setAcresAggregation,
		getBranchField,
		getRegionField,
		jobStatusList,
		testPackageList,
		submitterList,
		seasonList,
		branchList,
		regionList,
	} = params;

	return [
		textColumn("Grower", { accessorKeyOrFn: "growerName", size: 200 }),
		textColumn("Field", { accessorKeyOrFn: "fieldName" }),
		textColumn("Farm", { accessorKeyOrFn: "farmName" }),
		multiSelectColumn("Job Status", { accessorKeyOrFn: "status", filterSelectOptions: jobStatusList }),
		{
			id: "boundary acres",
			accessorFn: (row) => row.boundaryAcres ?? 0,
			header: "Boundary Acres",
			size: 100,
			filterVariant: "range",
			filterFn: "betweenInclusive",
			aggregationFn: "sum",
			Cell: ({ cell }) => (
				<Box
					component="span"
					sx={{ borderRadius: "0.25rem", maxWidth: "9ch", p: "0.25rem", textAlign: "right", display: "block" }}
				>
					{cell.getValue<number>() ? Math.round(cell.getValue<number>()) : "-"}
				</Box>
			),
			AggregatedCell: ({ row }) => {
				const leafRows = row.getLeafRows();
				const values = leafRows.map((r) => r.original.boundaryAcres).filter((v) => v != null && !isNaN(v));
				const sum = values.reduce((acc, v) => acc + v, 0);
				const average = values.length > 0 ? sum / values.length : 0;
				const displayValue = acresAggregation === "total" ? sum : average;
				const suffix = acresAggregation === "total" ? " total" : " average";

				return (
					<PortalTooltip title={`Click to show ${acresAggregation === "total" ? "average" : "total"}`}>
						<Box
							component="span"
							onClick={() => setAcresAggregation((prev) => (prev === "total" ? "average" : "total"))}
							sx={{
								cursor: "pointer",
								fontWeight: "bold",
								borderRadius: "0.25rem",
								color: "#000",
								maxWidth: "12ch",
								p: "0.25rem",
								textAlign: "right",
								display: "block",
							}}
						>
							{Math.round(displayValue)}
							{suffix}
						</Box>
					</PortalTooltip>
				);
			},
		} as MRT_ColumnDef<Job>,
		multiSelectColumn("Branch", { accessorKeyOrFn: getBranchField, filterSelectOptions: branchList }),
		multiSelectColumn("Region", { accessorKeyOrFn: getRegionField, filterSelectOptions: regionList }),
		dateColumn("Field Ready Date", { accessorKeyOrFn: "dateFieldReady", isRangeFilter: true }),
		dateColumn("Sample Date", { accessorKeyOrFn: "dateSampled", isRangeFilter: true }),
		dateColumn("Creation Date", { accessorKeyOrFn: "dateCreated", isRangeFilter: true }),
		dateColumn("Drop/Ship Date", { accessorKeyOrFn: "dateShipped", isRangeFilter: true }),
		multiSelectColumn("Test Package", { accessorKeyOrFn: "testPackage", filterSelectOptions: testPackageList }),
		checkboxColumn("Boundary Change?", { accessorKeyOrFn: "isBoundaryChanged" }),
		checkboxColumn("Boundary Approved?", { accessorKeyOrFn: "isBoundaryApproved" }),
		checkboxColumn("Points Change?", { accessorKeyOrFn: "isPointsChanged" }),
		multiSelectColumn("Submitter", { accessorKeyOrFn: "submitterName", filterSelectOptions: submitterList }),
		multiSelectColumn("Season", { accessorKeyOrFn: "season", filterSelectOptions: seasonList }),
		checkboxColumn("Approved Billing?", { accessorKeyOrFn: "isBillingApproved" }),
		checkboxMultiselectColumn("Job Flags", "Map Issue", "jobFlags"),
		dateColumn("Lab Sent Results", { accessorKeyOrFn: "dateLabResultsSent", isRangeFilter: true }),
		textColumn("Event ID", { accessorKeyOrFn: "eventId" }),
		textColumn("Lab Submittal ID", { accessorKeyOrFn: "labSubmittalId" }),
		numericColumn("Depth", { accessorKeyOrFn: "depth", aggregationFn: "sum", slider: false, sliderStepSize: 1 }),
		textColumn("Sample Changes", { accessorKeyOrFn: "sampleChanges" }),
		textColumn("Lab Name", { accessorKeyOrFn: "labName" }),
		checkboxColumn("Test Job?", { accessorKeyOrFn: "isTestJob" }),
		numericColumn("Samples", { accessorKeyOrFn: "numberOfSamples", aggregationFn: "sum", slider: false }),
	];
}

interface BuildDisplayColumnDefOptionsParams {
	setActionMenuElement: (el: HTMLElement | null) => void;
	setActionMenuRow: (row: MRT_Row<Job> | null) => void;
	grouping: MRT_GroupingState;
}

export function buildDisplayColumnDefOptions(params: BuildDisplayColumnDefOptionsParams) {
	const { setActionMenuElement, setActionMenuRow, grouping } = params;

	return {
		"mrt-row-select": {
			size: 40,
			visibleInShowHideMenu: true,
		},
		"mrt-row-actions": {
			header: "Files",
			size: 40,
			visibleInShowHideMenu: true,
			Header: () => {
				return (
					<Box className="download-button">
						<IconButton
							color="primary"
							onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
								setActionMenuElement(event?.currentTarget);
								setActionMenuRow(null);
							}}
						>
							<Download />
						</IconButton>
						<Box className="hide-on-small-screen">Files</Box>
					</Box>
				);
			},
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
				return (
					<Box className="download-button">
						<IconButton
							color="primary"
							onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
								setActionMenuElement(event?.currentTarget);
								setActionMenuRow(row);
							}}
						>
							<Download />
						</IconButton>
						<Box className="hide-on-small-screen">Files</Box>
					</Box>
				);
			},
			Cell: ({ row }: { row: MRT_Row<Job> }) => {
				return (
					<Box className="download-button">
						<IconButton
							color="primary"
							onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
								setActionMenuElement(event?.currentTarget);
								setActionMenuRow(row);
							}}
						>
							<Download />
						</IconButton>
						<Box className="hide-on-small-screen">Files</Box>
					</Box>
				);
			},
		},
		"mrt-row-expand": {
			Header: ({ table }: { table: any }) => (
				<Stack direction="row" alignItems="center">
					<MRT_ExpandAllButton table={table} />
					<Box>Groups</Box>
				</Stack>
			),
			// @ts-ignore
			GroupedCell: ({ row }) => {
				const value = row.getValue(grouping[grouping.length - 1]);
				if (value instanceof Date) {
					return value.toLocaleDateString();
				}
				return value;
			},
			Cell: ({ row, table }: { row: MRT_Row<Job>; table: any }) => {
				const groupingId = grouping[Math.min(row.depth, grouping.length - 1)];
				const value = row.getValue(groupingId);

				let label: string | unknown = value;
				if (value instanceof Date) {
					label = value.toLocaleDateString();
				}
				return (
					<Stack direction="row" alignItems="center">
						<MRT_ExpandButton row={row} table={table} />
						<Box>{label?.toString() || "n/a"}</Box>
					</Stack>
				);
			},
			muiTableBodyCellProps: ({ row }: { row: any }) => ({
				sx: (theme: any) => ({
					color:
						row.depth === 0 ? theme.palette.primary.main : row.depth === 1 ? theme.palette.secondary.main : undefined,
				}),
			}),
			size: 200,
			visibleInShowHideMenu: true,
		},
	};
}
